import type { SettlementBatchSummary } from "../types";
import { money, shortDateTime } from "../payrollPresentation";
import { useState, type ReactNode } from "react";
import { Button } from "./ui/Button";
import { PayrollSmsButton } from "./PayrollSmsButton";
import { usePlanningBaseline } from "./ui/DesignSystem";
import { RowActions } from "./ui/RowActions";
import { PayrollBatchWorkerTotals } from "./PayrollBatchWorkerTotals";

export function PayrollBatches({ batches, selectedId, disabled, onSelect, onDownload, onSms, onComplete, onRename, onDelete, renderExpanded }: { batches: SettlementBatchSummary[]; selectedId?: string; disabled: boolean; onSelect: (batch: SettlementBatchSummary) => void; onDownload: (batch: SettlementBatchSummary) => void; onSms: (batch: SettlementBatchSummary) => void; onComplete: (batch: SettlementBatchSummary) => void; onRename: (batch: SettlementBatchSummary, name: string) => void; onDelete: (batch: SettlementBatchSummary) => void; renderExpanded?: (batch: SettlementBatchSummary) => ReactNode }) {
  const baseline = usePlanningBaseline();
  /* 이름 고치기는 줄 안에서 끝난다. 팝업을 띄우면 이름 한 줄 바꾸자고 화면을 덮게 된다. */
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [closingContent, setClosingContent] = useState<Record<string, ReactNode>>({});
  const toggle = (batch: SettlementBatchSummary) => {
    // 닫히는 동안에는 마지막 내용을 남겨 높이가 자연스럽게 줄어들게 한다.
    const previous = batches.find(item => item.id === selectedId);
    if (previous && renderExpanded && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setClosingContent(current => ({ ...current, [previous.id]: renderExpanded(previous) }));
    }
    onSelect(batch);
  };
  const commit = (batch: SettlementBatchSummary) => {
    const next = editing?.name.trim() ?? "";
    if (next && next !== batch.name) onRename(batch, next);
    setEditing(null);
  };
  // The planning ledger uses the source's compact batch chips. Its prepared
  // batches move to history as soon as they are created (the source policy).
  /* 2026-10-19 밤 지윤 「내 것 없애지 말고 위에 해 봐」: 표준 목록 줄(Claude 커넥터·Skills 목록) 시안 — 줄 = 묶음 하나,
     왼쪽 이름(14/20 500) + 회색 한 줄(상태 · 건수 · 인원 · 금액 · 시각), 오른쪽 다음 할 일 하나(정산 완료) + ⋮(나머지).
     줄을 누르면 아래 카드와 같은 고르기. 카드는 비교용으로 그대로 아래에 남긴다. */
  const listRows = <ul className="payroll-batch-list" aria-label="정산 묶음">
    {batches.map(batch => {
      const sent = batch.messageCount > 0 && batch.messageSentCount >= batch.messageCount;
      return <li key={batch.id} className="payroll-batch-list-row" data-testid="settlement-batch-card" aria-current={batch.id === selectedId ? "true" : undefined}
        onClick={event => { if (!(event.target instanceof Element) || !event.target.closest("button, input, [role=menu]")) onSelect(batch); }}>
        <div className="payroll-batch-list-text">
          {editing?.id === batch.id ? <input autoFocus className="payroll-batch-rename" value={editing.name} maxLength={100} aria-label="묶음명" data-testid="rename-settlement-batch-input"
            onClick={event => event.stopPropagation()} onChange={event => setEditing({ id: batch.id, name: event.target.value })} onBlur={() => commit(batch)}
            onKeyDown={event => { if (event.key === "Enter") commit(batch); if (event.key === "Escape") setEditing(null); }} />
            : <strong>{batch.name}</strong>}
          <span data-testid="all-settlement-batch">{batch.status === "paid" ? "정산 완료" : "정산 대기"} · {batch.recordCount.toLocaleString()}건 · {batch.recipientCount.toLocaleString()}명 · {money(batch.payableAmount)} · {shortDateTime(batch.completedAt ?? batch.createdAt).replace(/^\d\d\. /, "")}</span>
          {!!batch.negativeWorkerNames?.length && <p className="payroll-batch-warning" role="status">{batch.negativeWorkerNames.join(", ")} 작업자의 공제 결과가 0원 미만이어서 이체액을 0원으로 처리했습니다.</p>}
        </div>
        <div className="payroll-batch-list-actions">
          {batch.status !== "paid" && <button type="button" className="account-settings-button" data-testid="complete-settlement-batch" disabled={disabled} onClick={() => onComplete(batch)}>정산 완료</button>}
          <RowActions label={batch.name} actions={[
            { label: "이체 파일 받기", onSelect: () => onDownload(batch) },
            { label: "이름 바꾸기", onSelect: () => setEditing({ id: batch.id, name: batch.name }) },
            { label: sent ? "정산 메시지(보냄)" : "정산 메시지", onSelect: () => onSms(batch), disabled: sent },
            { label: "삭제", onSelect: () => onDelete(batch), danger: true, disabled: disabled || batch.status === "paid" },
          ]} />
        </div>
      </li>;
    })}
  </ul>;
  /* 2026-10-19 밤 지윤: 카드는 걷고 목록 줄만 — 고른 묶음의 장부는 목록 바로 아래 하나. */
  if (baseline) return batches.length ? <>{listRows}
    {(() => { const chosen = batches.find(batch => batch.id === selectedId); return chosen ? <><PayrollBatchWorkerTotals batchId={chosen.id} revision={`${chosen.recordCount}:${chosen.payableAmount}`} />{renderExpanded?.(chosen)}</> : null; })()}
  </> : null;
  /* 묶음은 표 바로 위 한 줄씩 선다. 카드로 세우면 표 아래 멀리 떨어져, 골라도 효과가
     화면 밖에서 일어나고 화면만 길어졌다. 합계 일곱 가지 중 여기서 읽을 것은 상태·건수·
     이체액 셋이고, 나머지는 고른 뒤 표가 말한다. */
  return batches.length ? <div className="payroll-batch-strip">{batches.map(batch =>
    /* 줄 전체가 접기·펼치기를 맡고, 이름 변경과 오른쪽 동작 버튼만 자기 클릭을 가져간다. */
    <section key={batch.id} className="payroll-batch-group" data-selected={selectedId === batch.id || undefined} data-testid="settlement-batch-card">
    <div className="payroll-batch-line" role="button" tabIndex={0}
      aria-expanded={selectedId === batch.id}
      aria-controls={`payroll-batch-content-${batch.id}`}
      aria-label={`${batch.name} 정산 내역 ${selectedId === batch.id ? "접기" : "펼치기"}`}
      onClick={() => toggle(batch)}
      onKeyDown={event => { if ((event.key === "Enter" || event.key === " ") && event.target === event.currentTarget) { event.preventDefault(); toggle(batch); } }}
      data-selected={selectedId === batch.id || undefined}>
      <svg className="payroll-batch-chevron" viewBox="0 0 20 20" aria-hidden="true"><path d="M5 2 13 10 5 18" /></svg>
      <div className="payroll-batch-name">
        {/* 이름을 눌러도 묶음이 열린다. 이름 변경은 별도의 작은 버튼으로 분리한다. */}
        {editing?.id === batch.id
          ? <input autoFocus className="payroll-batch-rename" value={editing.name} maxLength={100} aria-label="묶음명"
              /* 입력 폭을 글자 수에 맞춘다. 고정 폭이면 편집을 시작할 때 옆의 요약이 밀려 줄이 흔들린다. */
              style={{ width: `${Math.max(10, editing.name.length + 1)}ch` }}
              data-testid="rename-settlement-batch-input"
              onClick={event => event.stopPropagation()}
              onChange={event => setEditing({ id: batch.id, name: event.target.value })}
              onBlur={() => commit(batch)}
              onKeyDown={event => { if (event.key === "Enter") commit(batch); if (event.key === "Escape") setEditing(null); }} />
          : <><strong>{batch.name}</strong><button type="button" className="payroll-batch-rename-icon" aria-label={`${batch.name} 이름 변경`} data-testid="rename-settlement-batch"
              onClick={event => { event.stopPropagation(); setEditing({ id: batch.id, name: batch.name }); }}>✎</button></>}
        {/* 이름과 요약 어느 쪽을 눌러도 같은 묶음 내역을 펼친다. */}
        <span className="payroll-batch-summary" data-testid="all-settlement-batch">
          <span className={`payroll-status ${batch.status}`}>{batch.status === "paid" ? "정산 완료" : "정산 대기"}</span>
          {/* 줄에서 확인할 값은 실제로 나갈 돈 하나다. 과세 대상액과 원천징수는 이체 파일과
              정산 내역 표가 이미 들고 있고, 셋을 나란히 적으면 줄이 두 줄로 넘어간다. */}
          <span>{batch.recordCount.toLocaleString()}건</span>
          <span>{batch.recipientCount.toLocaleString()}명</span>
          <b>{money(batch.payableAmount)}</b>
          <span>{shortDateTime(batch.completedAt ?? batch.createdAt).replace(/^\d\d\. /, "")}</span>
        </span>
      </div>
      {/* 끝난 동작도 자리를 지킨다. 버튼이 사라지면 "원래 없었나, 내가 이미 했나"를 화면이
          답해 주지 못한다. 비활성으로 남겨 두면 한 일과 남은 일이 같은 줄에서 읽힌다. */}
      <div className="payroll-batch-line-actions" onClick={event => event.stopPropagation()}>
        <Button size="compact" onClick={() => onDownload(batch)}>CSV</Button>
        <PayrollSmsButton batch={batch} onClick={() => onSms(batch)} />
        {/* 묶기를 취소하는 일이다. 이체를 마쳤으면 되돌릴 수 없다 — 같은 돈이 다음 묶음에
            다시 담겨 두 번 나갈 수 있다. */}
        <Button size="compact" variant="danger" disabled={disabled || batch.status === "paid"}
          title={batch.status === "paid" ? "이체를 마친 묶음은 삭제할 수 없습니다." : undefined}
          data-testid="delete-settlement-batch" onClick={() => onDelete(batch)}>삭제</Button>
        <Button size="compact" variant="primary" disabled={disabled || batch.status === "paid"}
          title={batch.status === "paid" ? `${shortDateTime(batch.completedAt ?? batch.createdAt)} 완료했습니다.` : undefined}
          data-testid="complete-settlement-batch" onClick={() => onComplete(batch)}>정산 완료</Button>
      </div>
    </div>
    {!!batch.negativeWorkerNames?.length && <p className="payroll-batch-warning" role="status">
      {batch.negativeWorkerNames.join(", ")} 작업자의 공제 결과가 0원 미만이어서 이체액을 0원으로 처리했습니다.
    </p>}
    <div id={`payroll-batch-content-${batch.id}`} className="payroll-batch-collapse" data-open={selectedId === batch.id}
      aria-hidden={selectedId !== batch.id} inert={selectedId !== batch.id}
      onTransitionEnd={event => {
        if (event.target === event.currentTarget && event.propertyName === "grid-template-rows" && selectedId !== batch.id) {
          setClosingContent(current => { const next = { ...current }; delete next[batch.id]; return next; });
        }
      }}>
      <div className="payroll-batch-collapse-clip">
        {(selectedId === batch.id || closingContent[batch.id]) && renderExpanded &&
          <div className="payroll-batch-body">{selectedId === batch.id ? <><PayrollBatchWorkerTotals batchId={batch.id} revision={`${batch.recordCount}:${batch.payableAmount}`} />{renderExpanded(batch)}</> : closingContent[batch.id]}</div>}
      </div>
    </div>
    </section>)}</div> : null;
}
